import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { LogOut, MessageSquare, Star, ShoppingBag, Layout, Image as ImageIcon, Users, Sparkles, Bell, Eye, EyeOff, ArrowRight, ChevronLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import axios from "axios";
import AdminClientsTab from "@/components/AdminClientsTab";
import AdminTransformationsTab from "@/components/AdminTransformationsTab";
import AdminAITab from "@/components/AdminAITab";
import AdminNotificationsTab from "@/components/AdminNotificationsTab";
import AdminMessagesTab from "@/components/AdminMessagesTab";
import AdminReviewsTab from "@/components/AdminReviewsTab";
import AdminProductsTab from "@/components/AdminProductsTab";
import AdminGalleryTab from "@/components/AdminGalleryTab";
import AdminContentTab from "@/components/AdminContentTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-transparent.png";

const BG_IMG = "https://images.unsplash.com/photo-1574679626212-7d11aa571832?w=1200&q=40";

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [stayLogged, setStayLogged] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const handleLogin = async (e) => {
    e.preventDefault(); setLoading(true); setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password, remember_me: stayLogged });
      const store = stayLogged ? localStorage : sessionStorage;
      store.setItem("gg_admin_token", data.token);
      if (!stayLogged) localStorage.setItem("gg_admin_session", "1");
      onLogin(data.token);
    } catch (err) { setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Login failed"); }
    setLoading(false);
  };
  return (
    <div className="min-h-screen bg-gg-black flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={BG_IMG} alt="" className="w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/80 to-black" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,59,48,0.05)_0%,transparent_70%)]" />
      </div>
      <motion.div initial={{ opacity: 0, scale: 0.96, filter: "blur(8px)" }} animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }} transition={{ duration: 0.5 }}
        className="w-full max-w-sm relative z-10">
        <motion.img src={LOGO_URL} alt="GG Fitness" className="h-14 mx-auto mb-6" initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} />
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="font-heading text-3xl font-black uppercase tracking-tighter text-center mb-1">Trainer Panel</motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }} className="text-zinc-500 text-sm text-center mb-8">Management access</motion.p>
        <AnimatePresence>
          {error && (
            <motion.p initial={{ opacity: 0, y: -10, height: 0 }} animate={{ opacity: 1, y: 0, height: "auto" }} exit={{ opacity: 0, y: -10, height: 0 }}
              className="text-red-400 text-sm text-center mb-4 bg-red-500/10 rounded-xl py-2.5 px-4 border border-red-500/20">{error}</motion.p>
          )}
        </AnimatePresence>
        <form onSubmit={handleLogin} className="space-y-4" data-testid="admin-login-form">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
            className="relative rounded-xl border border-white/10 bg-white/[0.03] focus-within:border-gg-red/50 focus-within:shadow-[0_0_20px_rgba(255,59,48,0.1)] transition-all">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="Email"
              className="w-full bg-transparent text-white h-13 px-4 text-sm outline-none placeholder:text-zinc-600" data-testid="admin-email-input" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
            className="relative rounded-xl border border-white/10 bg-white/[0.03] focus-within:border-gg-red/50 focus-within:shadow-[0_0_20px_rgba(255,59,48,0.1)] transition-all">
            <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password"
              className="w-full bg-transparent text-white h-13 px-4 pr-12 text-sm outline-none placeholder:text-zinc-600" data-testid="admin-password-input" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-600 hover:text-white transition-colors">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </motion.div>
          <motion.label initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}
            className="flex items-center gap-2.5 cursor-pointer select-none group" data-testid="admin-stay-logged">
            <div className={`w-4 h-4 rounded border transition-all ${stayLogged ? "bg-gg-red border-gg-red" : "border-white/20 group-hover:border-white/40"} flex items-center justify-center`}>
              {stayLogged && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 12"><path d="M10 3L4.5 8.5L2 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" /></svg>}
            </div>
            <input type="checkbox" checked={stayLogged} onChange={(e) => setStayLogged(e.target.checked)} className="hidden" />
            <span className="text-zinc-500 text-xs group-hover:text-zinc-300 transition-colors">Stay logged in</span>
          </motion.label>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}>
            <Button type="submit" disabled={loading} data-testid="admin-login-button"
              className="w-full bg-gg-red hover:bg-gg-red-hover text-white rounded-xl h-13 font-heading uppercase tracking-widest relative overflow-hidden group">
              <span className="relative z-10 flex items-center justify-center gap-2">
                {loading ? <Loader2 size={18} className="animate-spin" /> : "Login"}
                {!loading && <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-gg-red to-red-700 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </Button>
          </motion.div>
        </form>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.55 }}>
          <RouterLink to="/login" className="flex items-center gap-1 justify-center mt-6 text-zinc-600 text-xs hover:text-white transition-colors">
            <ChevronLeft size={14} /> Back
          </RouterLink>
        </motion.div>
      </motion.div>
    </div>
  );
}

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("gg_admin_token") || sessionStorage.getItem("gg_admin_token"));
  const [tab, setTab] = useState("clients");
  if (!token) return <AdminLogin onLogin={setToken} />;

  const tabs = [
    { id: "clients", label: "Members", icon: Users },
    { id: "notifications", label: "Notify", icon: Bell },
    { id: "transformations", label: "Transformations", icon: ImageIcon },
    { id: "messages", label: "Messages", icon: MessageSquare },
    { id: "reviews", label: "Reviews", icon: Star },
    { id: "products", label: "Products", icon: ShoppingBag },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
    { id: "ai", label: "AI Command", icon: Sparkles },
    { id: "content", label: "Content CMS", icon: Layout },
  ];

  return (
    <div className="min-h-screen bg-gg-black" data-testid="admin-dashboard">
      <div className="border-b border-white/10 bg-gg-surface">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="GG" className="h-8" />
            <span className="font-heading text-xs uppercase tracking-widest text-zinc-400">Trainer Panel</span>
          </div>
          <div className="flex items-center gap-3">
            <RouterLink to="/home" className="text-zinc-500 hover:text-white text-xs font-heading uppercase tracking-widest" data-testid="admin-view-site">View Site</RouterLink>
            <Button onClick={() => { localStorage.removeItem("gg_admin_token"); localStorage.removeItem("gg_admin_session"); sessionStorage.removeItem("gg_admin_token"); setToken(null); }} variant="ghost" size="sm" className="text-zinc-500 hover:text-red-500" data-testid="admin-logout-button"><LogOut size={16} /></Button>
          </div>
        </div>
      </div>
      <div className="border-b border-white/10">
        <div className="max-w-6xl mx-auto px-2 md:px-6 flex gap-0 overflow-x-auto scrollbar-none">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`admin-tab-${t.id}`} className={`flex items-center gap-1.5 px-3 md:px-5 py-3 font-heading text-[10px] md:text-xs uppercase tracking-wider md:tracking-widest whitespace-nowrap border-b-2 transition-colors shrink-0 ${tab === t.id ? "text-white border-gg-red" : "text-zinc-500 border-transparent hover:text-zinc-300"}`}>
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-6">
        {tab === "clients" && <AdminClientsTab token={token} />}
        {tab === "notifications" && <AdminNotificationsTab token={token} />}
        {tab === "transformations" && <AdminTransformationsTab token={token} />}
        {tab === "messages" && <AdminMessagesTab token={token} />}
        {tab === "reviews" && <AdminReviewsTab token={token} />}
        {tab === "products" && <AdminProductsTab token={token} />}
        {tab === "gallery" && <AdminGalleryTab token={token} />}
        {tab === "ai" && <AdminAITab token={token} />}
        {tab === "content" && <AdminContentTab token={token} />}
      </div>
    </div>
  );
}
